import React, {useEffect, useRef, useState} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import Map from '../Map';
import {gpxTraceService, GPXTrace, ElevationPoint} from '../../services/gpxTraceService';
import ElevationChart from './ElevationChart';
import './GpxViewer.css';

const PROFILE_HEIGHT = 160;

const GpxViewer: React.FC = () => {
    const {trace_gpx} = useParams<{ trace_gpx: string }>();
    const [traceMeta, setTraceMeta] = useState<GPXTrace | null>(null);
    const [profile, setProfile] = useState<ElevationPoint[] | null>(null);
    const [showProfile, setShowProfile] = useState(false);
    const [hoveredPosition, setHoveredPosition] = useState<{ lat: number; lon: number } | null>(null);
    const [headerHeight, setHeaderHeight] = useState(56);
    const headerRef = useRef<HTMLElement>(null);
    const navigate = useNavigate();

    // Observe header height changes (e.g. wrapping on mobile)
    useEffect(() => {
        if (!headerRef.current) return;
        const observer = new ResizeObserver(entries => {
            for (const entry of entries) {
                setHeaderHeight(entry.contentRect.height);
            }
        });
        observer.observe(headerRef.current);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (trace_gpx) {
            gpxTraceService.getTraceMeta(trace_gpx)
                .then(setTraceMeta)
                .catch(console.error);
        }
    }, [trace_gpx]);

    const handleToggleProfile = async () => {
        if (!trace_gpx) return;
        if (!showProfile && profile === null) {
            try {
                const data = await gpxTraceService.getElevationProfile(trace_gpx);
                setProfile(data.profile);
            } catch (e) {
                console.error(e);
                setProfile([]);
            }
        }
        setShowProfile(v => !v);
    };

    const hasStats = traceMeta && (
        traceMeta.distance_km !== null ||
        traceMeta.elevation_gain !== null
    );

    const mapHeight = showProfile
        ? `calc(100vh - ${headerHeight}px - ${PROFILE_HEIGHT}px)`
        : `calc(100vh - ${headerHeight}px)`;

    return (
        <div className="gpx-viewer">
            <header className="gpx-header" ref={headerRef}>
                <button
                    className="gpx-back-btn"
                    onClick={() => navigate('/')}
                    title="Retour à la carte"
                >
                    ← <span className="gpx-back-label">Carte</span>
                </button>
                <div className="gpx-trace-info">
                    {traceMeta ? (
                        <>
                            <strong title={traceMeta.original_name}>{traceMeta.original_name}</strong>
                            {traceMeta.description && traceMeta.description !== traceMeta.original_name.split('.')[0] && (
                                <span className="gpx-trace-description"> — {traceMeta.description}</span>
                            )}
                        </>
                    ) : (
                        <span className="gpx-trace-loading">Chargement…</span>
                    )}
                </div>
                {hasStats && (
                    <div className="gpx-stats">
                        {traceMeta.distance_km !== null && (
                            <span className="gpx-stat" title="Distance totale">
                                📏 {traceMeta.distance_km.toFixed(1)} km
                            </span>
                        )}
                        {traceMeta.elevation_gain !== null && (
                            <span className="gpx-stat gpx-stat--up" title="Dénivelé positif">
                                ↑ {Math.round(traceMeta.elevation_gain)} m
                            </span>
                        )}
                        {traceMeta.elevation_loss !== null && (
                            <span className="gpx-stat gpx-stat--down" title="Dénivelé négatif">
                                ↓ {Math.round(traceMeta.elevation_loss)} m
                            </span>
                        )}
                        <button
                            className={`gpx-profile-btn${showProfile ? ' active' : ''}`}
                            onClick={handleToggleProfile}
                            title="Afficher le profil altimétrique"
                        >
                            📈
                        </button>
                    </div>
                )}
                {traceMeta && (
                    <a
                        href={gpxTraceService.getTraceGPXUrl(traceMeta.path)}
                        download={traceMeta.original_name}
                        title="Télécharger le fichier GPX"
                        className="gpx-download-btn"
                    >
                        💾 <span className="gpx-download-label">Télécharger</span>
                    </a>
                )}
            </header>
            <Map traceGpxFile={trace_gpx} mapHeight={mapHeight} profileHoveredPosition={hoveredPosition}/>
            {showProfile && (
                <div className="gpx-elevation-panel" style={{ height: PROFILE_HEIGHT }}>
                    {profile === null ? (
                        <p className="gpx-elevation-loading">Chargement du profil…</p>
                    ) : (
                        <ElevationChart profile={profile} onHover={setHoveredPosition} />
                    )}
                </div>
            )}
        </div>
    );
};

export default GpxViewer;